/**
 * window-scroll-intent — binds the passive user-scroll feeders for the VERTICAL
 * bottom-image auto-scroll (DropdownView + ButtonColumnView dropdown mode) onto a
 * scroll context (the window, or an inner modal scroll body). It is the vertical
 * counterpart of `ColumnTrack`'s device-independent intent wiring (D34): the same
 * family of sources (`wheel` / `touchstart` / `keydown` on a scroll key / native
 * `scroll`) feed the SAME `ScrollIntentArbiter`, so a real user scroll suppresses /
 * aborts the auto-scroll exactly as on the horizontal axis. Factored out so both
 * vertical callers wire it identically (DRY) instead of two hand-rolled copies.
 *
 * The native `scroll` source is gated on `isProgrammatic` so the mechanism's OWN
 * smooth glide is not mistaken for user intent — mirroring `ColumnTrack.notifyScroll`.
 * Every listener is bound with the caller's `AbortSignal`, so the caller's existing
 * teardown (`AbortController.abort()` in `destroy()`) removes them in one call — no
 * per-listener removal, no leak across the modal's per-open re-mount.
 *
 * @see view/column-track.ts (the horizontal-axis equivalent, D34)
 * @see view/scroll-in-view.ts (the shared mechanism whose glide this gates against)
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/dropdown-bottom-image-autoscroll.md §3.4
 */

import { ScrollIntentArbiter } from './scroll-intent-arbiter';
import { ScrollInView } from './scroll-in-view';

/** Keys that scroll a focused scroll container — a keyboard user-scroll source (D34). */
const SCROLL_KEYS = new Set<string>([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

/** Whether `target` is a form control that consumes scroll keys for caret/typing. */
function isFormControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export interface WindowScrollIntentOptions {
  /** The intent gate fed by the user-scroll sources. */
  arbiter: ScrollIntentArbiter;
  /** The mechanism whose own glide must NOT be counted as user intent. */
  scrollInView: ScrollInView;
  /** Ties every listener's lifetime to the caller's teardown. */
  signal: AbortSignal;
  /**
   * The scroll context the `scroll` listener binds to: `null` ⇒ the window (the
   * document scrolls), otherwise an inner scroll element (the modal `.modal-body`).
   * The unambiguous gestures (wheel/touch/key) always bind to the window — they are
   * user intent regardless of which element ends up scrolling.
   */
  scrollContext: HTMLElement | null;
}

/**
 * Wire the passive window user-scroll feeders into `arbiter`. The `scroll` source
 * is gated on `scrollInView.isProgrammatic` so the auto-scroll's own glide does not
 * abort itself. All listeners are removed when `signal` aborts.
 */
export function bindWindowScrollIntent(options: WindowScrollIntentOptions): void {
  const { arbiter, scrollInView, signal, scrollContext } = options;
  const notify = (): void => arbiter.notifyUserScroll();
  const notifyKey = (event: KeyboardEvent): void => {
    if (isFormControl(event.target) || !SCROLL_KEYS.has(event.key)) return;
    arbiter.notifyUserScroll();
  };
  const notifyScroll = (): void => {
    if (!scrollInView.isProgrammatic) arbiter.notifyUserScroll();
  };

  const passive = { passive: true, signal } as const;
  window.addEventListener('wheel', notify, passive);
  window.addEventListener('touchstart', notify, passive);
  window.addEventListener('keydown', notifyKey, passive);
  // The native scroll source binds to whatever actually scrolls (window or the
  // inner modal body), gated so our own glide is not read as user intent.
  (scrollContext ?? window).addEventListener('scroll', notifyScroll, passive);
}
